import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell } from 'recharts';
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import type { Label } from '@/types/workflow-types';
import { getChartPaletteColor } from './helpers/model-colors';

const DIM_OPACITY = 0.28;

interface WorkflowLabelsPreviewSectionProps {
  labels: Label[];
  isLoading?: boolean;
  selectedLabelIds: string[];
  onToggleLabel: (labelId: string) => void;
  workflowCounts?: Record<string, number>;
}

export function WorkflowLabelsPreviewSection({
  labels,
  isLoading = false,
  selectedLabelIds,
  onToggleLabel,
  workflowCounts,
}: WorkflowLabelsPreviewSectionProps) {
  const selectedSet = useMemo(
    () => new Set(selectedLabelIds),
    [selectedLabelIds],
  );

  const chartData = useMemo(() => {
    if (!workflowCounts) return [];
    return labels
      .map((l) => ({
        id: l.id,
        name: l.name,
        count: workflowCounts[l.id] ?? 0,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
      .map((entry, idx) => ({ ...entry, color: getChartPaletteColor(idx) }));
  }, [labels, workflowCounts]);

  const totalWorkflows = useMemo(
    () => chartData.reduce((acc, d) => acc + d.count, 0),
    [chartData],
  );

  const chartConfig = useMemo(() => {
    const config: ChartConfig = { count: { label: 'Workflows' } };
    chartData.forEach((d) => {
      config[d.name] = { label: d.name, color: d.color };
    });
    return config;
  }, [chartData]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className='h-5 w-48' />
        </CardHeader>
        <CardContent>
          <Skeleton className='h-[260px] w-full' />
        </CardContent>
      </Card>
    );
  }

  if (labels.length === 0 || !workflowCounts) return null;

  return (
    <Card>
      <CardHeader className='pb-2'>
        <div className='flex items-start justify-between gap-4'>
          <div>
            <CardTitle className='text-base'>Workflows by Label</CardTitle>
            <p className='mt-1 text-xs text-muted-foreground'>
              Click a bar to drill into that label.
            </p>
          </div>
          <div className='text-right'>
            <div className='text-2xl font-bold leading-tight'>
              {totalWorkflows.toLocaleString()}
            </div>
            <div className='text-xs text-muted-foreground'>
              workflow-label associations, top {chartData.length} labels
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className='h-[280px] w-full'>
          <BarChart
            data={chartData}
            layout='vertical'
            margin={{ top: 4, right: 24, bottom: 4, left: 4 }}
          >
            <CartesianGrid horizontal={false} strokeDasharray='3 3' />
            <YAxis
              type='category'
              dataKey='name'
              width={140}
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={false}
            />
            <XAxis
              type='number'
              tickLine={false}
              axisLine={false}
              domain={[0, 'dataMax']}
              allowDecimals={false}
            />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Bar
              dataKey='count'
              radius={[0, 4, 4, 0]}
              cursor='pointer'
              onClick={(data) => {
                const id = (data as { payload?: { id?: string } }).payload?.id;
                if (id) onToggleLabel(id);
              }}
            >
              {chartData.map((entry) => {
                const isDimmed =
                  selectedSet.size > 0 && !selectedSet.has(entry.id);
                return (
                  <Cell
                    key={entry.id}
                    fill={entry.color}
                    fillOpacity={isDimmed ? DIM_OPACITY : 1}
                    stroke={selectedSet.has(entry.id) ? entry.color : undefined}
                    strokeWidth={selectedSet.has(entry.id) ? 2 : 0}
                  />
                );
              })}
            </Bar>
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
